import { FirstSection, ProductResultList, SecondSection, SimpleProductResultList } from "~/models/Product"
import { CommonSelectData, SelectData } from "~/models/SelectData"
import { kv } from "~/utils/ExtendFunctions"
import { ItemData, PriorityData } from "~/models/Csv"
import { Industries, IndustryType, isIndustry } from "~/models/Industry"
import { Company } from "~/models/Company";

/**
 * コンバート部品
 */
export const Converter = {

  /**
   * 選択中商品から未対応になった商品を削除して返します。
   * @param productResult 商品データ
   * @param selectData 選択データ
   * @returns 選択データ
   */
  removeInvalidSelected: (productResult: ProductResultList, selectData: SelectData): SelectData => {
    // 有効な商品IDを抽出
    let enableIdList: string[] = []
    Object.keys(productResult).forEach(key => {
      enableIdList = enableIdList.concat(productResult[key].map(product => product.id))
    })

    const adjustSelectData = { ...selectData }
    Object.keys(adjustSelectData).forEach(key => {
      const adjustCommon = { ...adjustSelectData[key] } as CommonSelectData
      if (adjustCommon.contracts) {
        const adjustContracts = {}
        Object.keys(adjustCommon.contracts).forEach(id => {
          if (enableIdList.includes(id)) {
            adjustContracts[id] = adjustCommon.contracts[id]
          }
        })
        adjustCommon.contracts = adjustContracts
        adjustSelectData[key] = adjustCommon
      }

      if (adjustCommon.ids) {
        const adjustIds: string[] = []
        adjustCommon.ids.forEach(id => {
          if (enableIdList.includes(id)) {
            adjustIds.push(id)
          }
        })
        adjustCommon.ids = adjustIds
        adjustSelectData[key] = adjustCommon
      }
    })

    return adjustSelectData
  },

  /**
   * APIから取得した商品カタログを画面表示用に加工して返します。
   * @param industries 選択事業
   * @param productResult 商品カタログデータ
   * @param itemDataList CSVアイテムデータリスト
   * @param priorityDataList CSV優先順位データリスト
   * @param company
   * @returns 画面表示用商品カタログデータ
   */
  toSimpleProductResult: (
    industries: Industries,
    productResult: ProductResultList,
    itemDataList: ItemData[],
    priorityDataList: PriorityData[],
    company: Company
  ): SimpleProductResultList => {
    const industryIds = Object.keys(industries)

    const fill = (item: ItemData) => ({
      point: item.point,
      description: item.description,
      modalType: item.modalType,
      selected: item.selected,
      htmlKey: item.htmlKey,
      requiredModalExplanation: item.requiredModalExplanation,
      uncheckExplanation: item.uncheckExplanation,
      condition: item.condition,
      section1: item.section1,
      section2: item.section2
    })

    const flatProductResult = kv(Object.values(productResult).flat())

    const containsMaintenance = industryIds.includes(IndustryType.Maintenance)
    const containsService = industryIds.includes(IndustryType.Service)
    const containsRepair = industryIds.includes(IndustryType.Repair)
    const containsCarSales = industryIds.includes(IndustryType.CarSales)

    // 基本サービス（整備、SS、車販）
    const ssBasic = (() => {
      // 整備またはSS選択時
      if (containsMaintenance || containsService) {
        return itemDataList
          .filter(isIndustry([FirstSection.Basic], [SecondSection.Any, SecondSection.Maintenance]))
          .map((item) => ({ ...flatProductResult[item.id], ...fill(item) }))
          .filter((item) => item)
      }
      // 車販選択時
      if (containsCarSales) {
        return itemDataList
          .filter(isIndustry([FirstSection.Basic], [SecondSection.Any]))
          .map((item) => ({ ...flatProductResult[item.id], ...fill(item) }))
          .filter((item) => item)
      }
      return []
    })()

    // 基本サービス（板金）
    const reBasic = containsRepair ? itemDataList
      .filter(isIndustry([FirstSection.Basic], [SecondSection.Repair]))
      .map((item) => ({ ...flatProductResult[item.id], ...fill(item) }))
      .filter((item) => item) : []

    // 基本サービス（共通）は全ての事業で対象
    const coBasic = itemDataList
      .filter(isIndustry([FirstSection.Basic], [SecondSection.Shared]))
      .map((item) => ({ ...flatProductResult[item.id], ...fill(item) }))
      .filter((item) => item)

    const createSectionData = (section1: FirstSection, force: boolean = false) => {
      const sortData = {
        sort1: ["1"],
        sort2: ["3"],
        sort3: ["2"],
        sort4: ["1", "3"],
        sort5: ["1", "2"],
        sort6: ["2", "3"],
        sort7: ["1", "2", "3"]
      } as const
      const sortType = (Object.keys(sortData).filter(key => sortData[key].toString() === industryIds.toString())).join(",") || "sort7"
      const sort = Object.fromEntries(priorityDataList.filter((item) => item.section1 === section1).map((item) => [item.id, item[sortType]]))

      const convertIds = {
        [SecondSection.Any]: [IndustryType.Maintenance, IndustryType.Repair, IndustryType.Service, IndustryType.CarSales],
        [SecondSection.Maintenance]: [IndustryType.Maintenance, IndustryType.Service],
        [SecondSection.Repair]: [IndustryType.Repair],
        [SecondSection.Shared]: [IndustryType.Maintenance, IndustryType.Repair, IndustryType.Service],
        [SecondSection.CarSales]: [IndustryType.CarSales],
        [SecondSection.None]: [],
      } as const

      return itemDataList
        .filter((item) => {
          if (force) {
            return item.section1 === section1;
          }
          return item.section1 === section1
            && convertIds[item.section2].some((id: string) => industryIds.includes(id));
        })
        .map((item) => {
          return ({
            ...flatProductResult[item.id],
            ...fill(item),
            sort: sort[item.id] | Number.MAX_VALUE
          })
        })
        .filter((item) => item)
        .sort((a, b) => {
          return (a.sort || Number.MAX_VALUE) - (b.sort || Number.MAX_VALUE)
        })
    }
    // 2：従業員パック
    const staff = createSectionData(FirstSection.Staff)

    // 3：提案力強化のためのオプション
    const planning = createSectionData(FirstSection.Planning)

    // 4：生産性強化のためのオプション
    const performance = createSectionData(FirstSection.Performance)

    // 5：DX推進のためのオプション
    const dx = createSectionData(FirstSection.DigitalTransformation)

    // 6：集客強化のためのオプション
    const marketing = createSectionData(FirstSection.Marketing)

    // 7：データベース
    const database = createSectionData(FirstSection.Database)

    // 8：メンテナンスサポート
    // 9：初期導入支援（全業種共通）
    const maintenanceSup = [...createSectionData(FirstSection.MaintenanceSupport), ...createSectionData(FirstSection.IntroductionSupport)]

    // 10：電帳.DX向け商品
    const edBasic = createSectionData(FirstSection.Dencho, true)

    // 11：メンテナンスサポート・初期導入支援（電帳向け）
    const edMaintenanceSup = createSectionData(FirstSection.DenchoSupport, true)

    // 12：プライスボード作成支援サービス向け商品
    const ppBasic = createSectionData(FirstSection.PricePrint, company.priceBoardService)

    // 13：マーケットプレイス
    const mpBasic = createSectionData(FirstSection.MarketPlace, company.marketPlace)

    // 14：共通オプション
    const common = createSectionData(FirstSection.Shared)

    // 15：BLPay
    const payBasic = createSectionData(FirstSection.BroadLeafPay, company.broadLeafPay)

    return {
      ssBasic,
      reBasic,
      coBasic,
      staff,
      planning,
      performance,
      dx,
      marketing,
      database,
      maintenanceSup,
      edBasic,
      edMaintenanceSup,
      ppBasic,
      mpBasic,
      common,
      payBasic,
    };
  }
}
